import { Auth0ContextInterface } from "@auth0/auth0-react";
import { IServerSideGetRowsParams } from "ag-grid-community";
import axios from "axios";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import setupSignalRConnection from "../lib/signalRHubConnection";
import { ActionTypes as types } from "./actionTypes";
import {
  DependencyCheckClientAgent,
  DependencyCheckClient,
  DependencyData,
  RestorePendingDependencies,
  CheckIsReadyRequest,
  CheckByFilterRequest,
  DailyValuationKickoffRequest,
  ForceByFilterOutcome,
  ForceByFilterResponse,
  ForceMode,
  toCheckIsReadyRequest,
} from "./types";
import { getRowData } from "./rowDataUtils";
import { statusCountsActions } from "./statusCountsSlice";
import { RootState } from ".";
import { enqueueSnackbar as enqueueSnackbarAction } from "../store/snackbarActions";

const cozonacApiUrl = process.env.REACT_APP_COZONAC_API_URL;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const actions = {
  fetchDependencies(auth0Context: Auth0ContextInterface): AppThunk {
    return async (dispatch) => {
      dispatch({ type: types.REQUEST_DEPENDENCIES, data: auth0Context });
      const { getAccessTokenSilently } = auth0Context;
      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.get(`${cozonacApiUrl}/api/dependencies`, {
          headers: {
            Authorization: `bearer ${token}`,
          },
        });

        dispatch({
          type: types.RECEIVED_DEPENDENCIES_SUCCESS,
          data: resp.data,
        });
      } catch (err) {
        dispatch({
          type: types.RECEIVED_DEPENDENCIES_ERROR,
          error: getErrorString(err),
        });
      }
    };
  },

  fetchDependenciesDatasource(
    auth0Context: Auth0ContextInterface,
    params: IServerSideGetRowsParams
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.REQUEST_DEPENDENCIES,
        data: params.request,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;
      try {
        const token = await getAccessTokenSilently();

        // Skip count on subsequent page loads (when startRow > 0) to improve performance
        const requestWithSkipCount = {
          ...params.request,
          skipCount: (params.request.startRow ?? 0) > 0,
        };

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/dataSource`,
          requestWithSkipCount,
          {
            headers: {
              Authorization: `bearer ${token}`,
            },
          }
        );

        dispatch({
          type: types.RECEIVED_DEPENDENCIES_SUCCESS,
          data: resp.data,
          error: null,
        });

        const flatRows = getRowData(resp.data.rowData);
        params.success({
          rowData: flatRows,
          ...(resp.data.rowCount != null && { rowCount: resp.data.rowCount }),
        });

        // Fire-and-forget: the rows are already rendered, the progress markers follow. A
        // failure here must never reach params.fail().
        try {
          dispatch(statusCountsActions.requestForRows(auth0Context, flatRows));
        } catch (countsErr) {
          console.warn("Status counts request could not be queued", countsErr);
        }
      } catch (err) {
        dispatch({
          type: types.RECEIVED_DEPENDENCIES_ERROR,
          error: getErrorString(err),
        });
        params.fail();
      }
    };
  },

  setupSignalRHub(auth0Context: Auth0ContextInterface): AppThunk {
    return async (dispatch) => {
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();
        dispatch({ type: types.REQUEST_CONNECTION_INFO, data: token });
        const resp = await axios.get(
          `${cozonacApiUrl}/api/hub/negotiate/dependencies`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_CONNECTION_INFO_SUCCESS,
          data: resp.data,
        });

        dispatch({ type: types.REQUEST_HUB_CONNECTION, data: resp.data });

        const setupEventsHub = await setupSignalRConnection(
          resp.data.url,
          {
            onMessageEvent: (message: DependencyData) => {
              const action = { type: types.RECEIVED_MESSAGE, data: message };
              dispatch(action);
              return action;
            },
          },
          resp.data.accessToken
        );
        dispatch(setupEventsHub);
      } catch (err) {
        dispatch({
          type: types.RECEIVED_CONNECTION_INFO_ERROR,
          error: getErrorString(err),
        });
      }
    };
  },

  checkDependencyExists(
    auth0Context: Auth0ContextInterface,
    dependencies: Array<DependencyData>
  ): AppThunk {
    return async (dispatch) => {
      // Convert DependencyData to CheckIsReadyRequest format with routing info
      const payload: CheckIsReadyRequest[] = dependencies.map(toCheckIsReadyRequest);
      
      dispatch({
        type: types.REQUEST_CHECK_DEPENDENCY,
        data: payload,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/status`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Check Is Ready queued for ${resp.data?.count ?? payload.length} dependencies`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Error checking dependencies`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  abandonDependencies(
    auth0Context: Auth0ContextInterface,
    payload: Array<DependencyData>
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.REQUEST_ABANDON_DEPENDENCY,
        data: payload,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/abandon`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_ABANDON_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Successfully abandoned dependencies`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RECEIVED_ABANDON_DEPENDENCY_ERROR,
          error: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Abandon dependencies failed`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  forceClientDependency(
    auth0Context: Auth0ContextInterface,
    payload: Array<DependencyCheckClient>
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.REQUEST_CHECK_DEPENDENCY,
        data: payload,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/skipClientDependencyCheck`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Successfully sent message to skip dependency check for selected clients`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: getApiErrorMessage(err, `Error skipping client dependencies`),
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  forceClientAgentDependency(
    auth0Context: Auth0ContextInterface,
    payload: Array<DependencyCheckClientAgent>
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.REQUEST_CHECK_DEPENDENCY,
        data: payload,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/skipClientAgentDependencyCheck`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Successfully sent message to skip dependency check for selected clients`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: getApiErrorMessage(err, `Error skipping client dependencies`),
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  restorePendingDependencies(
    auth0Context: Auth0ContextInterface,
    payload: Array<RestorePendingDependencies>
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.RESTORE_PENDING_DEPENDENCY,
        data: payload,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/restorePendingDependencies`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RESTORE_PENDING_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Successfully sent restore pending dependencies command`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RESTORE_PENDING_DEPENDENCY_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Error sending restore pending dependencies command`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  dailyValuationKickoff(
    auth0Context: Auth0ContextInterface,
    request: DailyValuationKickoffRequest
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.DAILY_VALUATION_KICKOFF,
        data: request,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/dailyValuationKickoff`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.DAILY_VALUATION_KICKOFF_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Successfully triggered daily valuation kickoff`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.DAILY_VALUATION_KICKOFF_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Error triggering daily valuation kickoff`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  exportDependenciesToCsv(
    auth0Context: Auth0ContextInterface,
    request: {
      filterModel: Record<string, unknown>;
      sortModel?: Array<{ colId: string; sort: string | null | undefined }>;
      rowGroupCols?: Array<{ id: string; displayName: string; field: string }>;
      groupKeys?: string[];
      visibleColumns?: string[];
    }
  ): AppThunk {
    return async (dispatch) => {
      try {
        const { getAccessTokenSilently } = auth0Context;
        const token = await getAccessTokenSilently();
        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/export`,
          request,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          }
        );
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `dependencies-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch (err) {
        dispatch(
          enqueueSnackbarAction({
            message: `Error exporting dependencies to CSV: ${getErrorString(err)}`,
            key: new Date().getTime() + Math.random(),
            options: { variant: 'error' },
          })
        );
      }
    };
  },

  /**
   * Check dependencies by filter criteria instead of explicit row IDs.
   * This allows running actions on groups without loading all child rows.
   */
  checkDependenciesByFilter(
    auth0Context: Auth0ContextInterface,
    filter: CheckByFilterRequest
  ): AppThunk {
    return async (dispatch) => {
      dispatch({
        type: types.REQUEST_CHECK_DEPENDENCY,
        data: filter,
        error: null,
      });
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post(
          `${cozonacApiUrl}/api/dependencies/status/byFilter`,
          filter,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_SUCCESS,
          data: resp.data,
          error: null,
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Check Is Ready queued for ${resp.data?.count ?? 'filtered'} dependencies`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "success",
            },
          })
        );
      } catch (err) {
        dispatch({
          type: types.RECEIVED_CHECK_DEPENDENCY_ERROR,
          data: getErrorString(err),
        });
        dispatch(
          enqueueSnackbarAction({
            message: `Error checking dependencies by filter`,
            key: new Date().getTime() + Math.random(),
            options: {
              variant: "error",
            },
          })
        );
      }
    };
  },

  /**
   * Group-level Force (PMDEV-3736). Posts the group's filter criteria to
   * skipClient(Agent)DependencyCheck/byFilter. A dry run only returns the counts (no
   * snackbar); an execute shows the API's message, or its error msg, in a snackbar.
   */
  forceByFilter(
    auth0Context: Auth0ContextInterface,
    mode: ForceMode,
    filter: CheckByFilterRequest,
    dryRun: boolean
  ): AppThunk<Promise<ForceByFilterOutcome>> {
    return async (dispatch) => {
      const endpoint = mode === "Client" ? "skipClientDependencyCheck" : "skipClientAgentDependencyCheck";
      const fallbackError = `Error forcing ${mode === "Client" ? "Client" : "Client Agent"} dependencies for the group`;
      const { getAccessTokenSilently } = auth0Context;

      try {
        const token = await getAccessTokenSilently();

        const resp = await axios.post<ForceByFilterResponse>(
          `${cozonacApiUrl}/api/dependencies/${endpoint}/byFilter${dryRun ? "?dryRun=true" : ""}`,
          filter,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!dryRun) {
          dispatch(
            enqueueSnackbarAction({
              message: resp.data?.message ?? `Force queued`,
              key: new Date().getTime() + Math.random(),
              options: {
                variant: "success",
              },
            })
          );
        }

        return { data: resp.data };
      } catch (err) {
        const error = getApiErrorMessage(err, fallbackError);

        if (!dryRun) {
          dispatch(
            enqueueSnackbarAction({
              message: error,
              key: new Date().getTime() + Math.random(),
              options: {
                variant: "error",
              },
            })
          );
        }

        return { error };
      }
    };
  },
};

/**
 * The API's `{ msg }` error text (e.g. a Force 400 explaining why it was rejected), or the
 * fallback when the response carries none.
 */
export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const msg = (err as { response?: { data?: { msg?: unknown } } })?.response?.data?.msg;
  return typeof msg === "string" && msg.length > 0 ? msg : fallback;
};

const getErrorString = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  } else if (typeof err === "string") {
    return err;
  } else {
    return "Unknown error";
  }
};
